<template>
  <div class="m-4" style="width: 100%;">
    <el-cascader
      v-model="refValue"
      ref="cascaderRef"
      :options="refOptions"
      :props="props1"
      style="width: 100%;"
      clearable
      filterable
      :filter-method="customFilter"
      @change="handleChange"
    >
      <template #footer>
        <div style="text-align: right;">
          <el-button link size="small" @click="hideToggle">确定</el-button>
        </div>
      </template>
    </el-cascader>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, defineProps, onMounted, watch, defineEmits, nextTick } from 'vue'
import { apiFetchData } from '../utils'

export default defineComponent({
  name: 'Cascader3Selector'
})
</script>

<script lang="ts" setup>
const props = defineProps({
  dataValue: {
    type: String,
    require: true
  },
  srcDataUrl: {
    type: String,
    require: true
  }
})

const props1 = {
  showPrefix: false,
  // 多选任意层级
  // multiple: true,
  // 出现单选框，选择任意层级
  checkStrictly: true,
  // 点击时直接选中
  checkOnClickNode: true,
  // 点击任何节点（包括中间节点和叶子节点）都会触发选中/取消选中
  // 点击中间节点时，会选中该节点及其所有子节点
  // 点击叶子节点时，只选中该叶子节点
  checkOnClickLeaf: true,
  expandTrigger: 'click'
}

const cascaderRef = ref()
const refValue = ref('')
const refOptions = ref([] as object)
// mounted时
onMounted(async () => {
  const data = await apiFetchData(props.srcDataUrl)
  const result = [] as object[]
  transformToCascaderData(data.data, [], result)
  refOptions.value = result
  refValue.value = props.dataValue?.split('/')
})

// 主动实现 通知到 父组件
const emit = defineEmits(['update:modelValue']) // 定义 'update:modelValue' 事件

// 监听 refValue 的变化，并触发事件
watch(refValue, (newValue) => {
  // console.log(refValue, newValue)
  emit('update:modelValue', newValue) // 当 refValue 改变时触发事件
  // console.log('refValue=', refValue, 'newValue=', newValue)
})

const customFilter = (node: any, keyword: string) => {
  const lowerText = node.text.toLowerCase()
  const keyArr = keyword.toLowerCase().split(' ').filter(key => key.trim() !== '')

  return keyArr.every(key => lowerText.includes(key))
}

const hideToggle = () => {
  if (cascaderRef.value) {
    // 关闭下拉面板
    cascaderRef.value.togglePopperVisible(false)
  }
}

const findNodeByPath = (options, path) => {
  let resultNode = null
  let currentNodes = options

  for (let i = 0; i < path.length; i++) {
    const targetValue = path[i]
    const foundNode = currentNodes.find(node => node.value === targetValue)

    if (!foundNode) {
      return null // 路径中的某个节点不存在
    }

    resultNode = foundNode
    currentNodes = foundNode.children || []
  }

  return resultNode
}

const handleChange = (val) => {
  console.log('handleChange=', val)

  if (!val) {
    return
  }
  nextTick(() => {
    const currentNode = findNodeByPath(refOptions.value, val)
    console.log('currentNode=', currentNode)
    if (currentNode && !currentNode.children) {
      hideToggle()
    }
  })
}
// 提取后面的 {   level1: 'China', level1_name: '中国', level2: '中国', level2_name: '中国', level3: '中国', level3_name: '中国' }
// 第二个元素后的内容
function extractByKeysInOrder<T extends Record<string, any>> (obj: T): Partial<T> {
  const result: Partial<T> = {}
  const allKeys = Object.keys(obj) as Array<keyof T>
  for (let i = 2; i < allKeys.length; i++) {
    const key = allKeys[i]
    result[key] = obj[key]
  }

  return result
}

function transformDict (data:object, pathArr: string[], result: object[]) {
  const valueList = Object.values(data)
  const valueKey = valueList[0]
  const label = valueList[1]

  // 后面的数据是不是都相同
  let isDiff = false
  for (let j = 2; j < valueList.length; j += 2) {
    const valueKey2 = valueList[j]
    if (valueKey !== valueKey2) {
      isDiff = true
      break
    }
  }

  // 提取后面你的子数据
  const subObj = extractByKeysInOrder(data)

  // 当前节点是不是已经存在了
  const subPathArr = pathArr ? [...pathArr, valueKey] : valueKey
  const subNode = findNodeByPath(result, subPathArr)
  if (subNode) {
    // 已存在则直接处理后面的数据
    if (subObj && isDiff) {
      transformDict(subObj, subPathArr, result)
    }
    return
  }

  // 找到父节点
  const currentNode = findNodeByPath(result, pathArr)
  // 没有父节点，则使用根节点
  let child = currentNode ? currentNode.children : result
  if (!child) {
    currentNode.children = []
    child = currentNode.children
  }
  // 插入数据
  child.push({
    value: valueKey,
    label: label
    // children: []
  })

  if (!isDiff) {
    // 后面都是重复数据返回
    return
  }

  if (subObj) {
    console.log('transformToCascaderData subObj', subObj)
    transformDict(subObj, subPathArr, result)
  }
}
/**
 * 将任意字段的对象数组转换为级联选择器数据
 * @param data 原始数据数组，字段按 value1, label1, value2, label2, ... 的顺序
 * @returns ElementUI Cascader 格式的树形数据
 */
function transformToCascaderData (data: object[], pathArr: string[], result: object[]) {
  for (let i = 0; i < data.length; i++) {
    transformDict(data[i], pathArr, result)
  }
}
</script>
